/**
 * Color helpers (Phase 4.4c-1).
 *
 * Pure functions used by the theme-generator to derive the semantic
 * primary aliases (`--color-primary-fg`, `--color-primary-hover`,
 * `--color-primary-soft`) from the active preset's primary[500].
 *
 * These mirror the math the approved design uses in
 * tools/design-studio/src/designs/approved/auth-and-onboarding/
 *   _shared/legacy.tsx (functions `pickFg` and `shade`). Lifted here so
 * production code does not depend on design-studio scaffolding.
 *
 * Both functions accept and return uppercase hex strings of the form
 * `#RRGGBB`. Inputs may include or omit the leading `#`. Outputs always
 * include `#`. Inputs that don't parse as a 6-digit hex collapse to a
 * safe fallback (mid-grey) so a misconfigured preset never crashes the
 * theme generator at load time.
 */

const FALLBACK_HEX = '#888888';

interface Rgb {
  r: number;
  g: number;
  b: number;
}

function parseHex(input: string): Rgb | null {
  const stripped = input.replace('#', '').trim();
  if (!/^[0-9a-fA-F]{6}$/.test(stripped)) return null;
  return {
    r: parseInt(stripped.slice(0, 2), 16),
    g: parseInt(stripped.slice(2, 4), 16),
    b: parseInt(stripped.slice(4, 6), 16),
  };
}

function rgbToHex({ r, g, b }: Rgb): string {
  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('')}`;
}

/**
 * Pick a foreground color (#111 or #fff) for legible text on top of
 * the given background. Uses the WCAG relative-luminance formula
 * (0.2126 R + 0.7152 G + 0.0722 B) on the linear-light values.
 *
 * The foreground is selected by whichever of near-black or white produces
 * the higher WCAG contrast ratio. This keeps brighter accent colors usable
 * without forcing every primary action into a dark shade.
 */
export function pickFg(hex: string): string {
  const rgb = parseHex(hex);
  if (!rgb) return '#FFFFFF';
  const toLinear = (channel: number) => {
    const normalized = channel / 255;
    return normalized <= 0.03928
      ? normalized / 12.92
      : ((normalized + 0.055) / 1.055) ** 2.4;
  };
  const luminance =
    0.2126 * toLinear(rgb.r) + 0.7152 * toLinear(rgb.g) + 0.0722 * toLinear(rgb.b);
  const darkLuminance = 0.2126 * toLinear(17) + 0.7152 * toLinear(17) + 0.0722 * toLinear(17);
  const whiteContrast = (1 + 0.05) / (luminance + 0.05);
  const darkContrast = (luminance + 0.05) / (darkLuminance + 0.05);

  return whiteContrast >= darkContrast ? '#FFFFFF' : '#111111';
}

/**
 * Lighten or darken a hex color by `amount` (range -1..+1).
 *   amount > 0 → mix toward white by that fraction of the remaining range
 *   amount < 0 → scale RGB channels down by (1 + amount)
 *   amount = 0 → return the input verbatim (with `#` prefix normalized)
 *
 * Same implementation as the design-studio's `shade()` so production
 * output matches the prototype to the byte. Inputs that fail to parse
 * collapse to FALLBACK_HEX so callers never receive `null`.
 */
export function shade(hex: string, amount: number): string {
  const rgb = parseHex(hex);
  if (!rgb) return FALLBACK_HEX;
  if (amount === 0) return rgbToHex(rgb);

  let { r, g, b } = rgb;
  if (amount > 0) {
    r = r + (255 - r) * amount;
    g = g + (255 - g) * amount;
    b = b + (255 - b) * amount;
  } else {
    const k = 1 + amount;
    r = r * k;
    g = g * k;
    b = b * k;
  }
  return rgbToHex({ r, g, b });
}
